<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
        <BaseInput
          v-model="modifiedData.name"
          color="base"
          show-error-as-tooltip
          :error="getInputErrors('name')" />
      </div>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
        <BaseInput
          v-model="modifiedData.code"
          color="base"
          show-error-as-tooltip
          :error="getInputErrors('code')" />
      </div>

      <BaseToggle
        :label="$t('baseObject.isActive')"
        name="isActive"
        v-model="modifiedData.isActive"
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, numeric } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import { addOrEditSalespersonType } from '@/services/api/user';
import type { SalesPersonType } from '@/types/SalesPersons';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    salespersonType: SalesPersonType;
    salespersonTypes?: SalesPersonType[];
  }>(),
  {}
);

const modifiedData = ref<SalesPersonType>(deepCopy<SalesPersonType>(props.salespersonType));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';

    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.salespersonType);
    isModified.value = valueChanged;
  },
  { deep: true }
);

function onDismissChanges() {
  modifiedData.value = deepCopy<SalesPersonType>(props.salespersonType);
  emit('closeModal');
}

const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await addOrEditSalespersonType(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: 'Record already exists' });
  }
}

const validationModel = ref({
  name: modifiedData.value.name || '',
  code: modifiedData.value.code || '',
});

const rules = computed(() => ({
  name: {
    required: helpers.withMessage('Name is required', required),
    isUnique: helpers.withMessage(
      'Name should be unique',
      (value: string) =>
        !props.salespersonTypes?.map((x) => x.name).includes(value) || props.mode == 'edit'
    ),
  },
  code: {
    required: helpers.withMessage('Code is required', required),
    numeric: helpers.withMessage('Code should be a number', numeric),
    isUnique: helpers.withMessage(
      'Code should be unique',
      (value: string) =>
        !props.salespersonTypes?.map((x) => x.code).includes(value) || props.mode == 'edit'
    ),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function getInputErrors(type: 'name' | 'code') {
  if ($v.value[type].required.$invalid) return $v.value[type].required.$message;
  else if (type == 'code' && $v.value[type].numeric.$invalid) return $v.value.code.numeric.$message;
  else if ($v.value[type].isUnique.$invalid) return $v.value[type].isUnique.$message;
  else return '';
}
</script>
